import type { FilterNode } from "@/lib/contracts/nl";
import type { QueryPlan } from "./types";

export interface QueryProvenanceItem {
  label: "Operation" | "Columns" | "Filter" | "Rows" | "Geography";
  value: string;
}

const OP_LABELS: Record<string, string> = {
  eq: "=",
  neq: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  in: "is one of",
  contains: "contains",
  geo_in_region: "is in region",
  geo_within_km: "is within km",
};

function displayValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(displayValue).join(", ");
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value) ?? "—";
}

function describeFilterNode(node: FilterNode, nested: boolean): string {
  if ("all" in node) {
    const text = node.all.map((child) => describeFilterNode(child, true)).join(" AND ");
    return nested ? `(${text})` : text;
  }
  if ("any" in node) {
    const text = node.any.map((child) => describeFilterNode(child, true)).join(" OR ");
    return nested ? `(${text})` : text;
  }
  if ("not" in node) return `NOT (${describeFilterNode(node.not, false)})`;
  return `${node.field} ${OP_LABELS[node.op] ?? node.op} ${displayValue(node.value)}`;
}

export function describeFilter(node: FilterNode): string {
  return describeFilterNode(node, false);
}

function filterColumns(node: FilterNode): string[] {
  if ("all" in node) return node.all.flatMap(filterColumns);
  if ("any" in node) return node.any.flatMap(filterColumns);
  if ("not" in node) return filterColumns(node.not);
  return [node.field];
}

function operation(plan: QueryPlan): string {
  if (plan.aggregate) {
    const fn = plan.aggregate.fn === "avg" ? "Average" : `${plan.aggregate.fn[0]!.toUpperCase()}${plan.aggregate.fn.slice(1)}`;
    const target = plan.aggregate.fn === "count" ? "rows" : plan.aggregate.column;
    return `${fn} ${target ?? "values"}${plan.aggregate.groupBy ? ` by ${plan.aggregate.groupBy}` : ""}`;
  }
  if (plan.topN) return `${plan.topN.asc ? "Bottom" : "Top"} ${plan.topN.n} rows by ${plan.topN.column}`;
  if (plan.spatial?.kind === "within_radius") return `Find rows within ${plan.spatial.km ?? 0} km`;
  if (plan.spatial?.kind === "nearest_n") return `Find ${plan.spatial.n ?? 1} nearest rows`;
  if (plan.spatial?.kind === "distance_to") return "Calculate distance for each row";
  return "Find matching rows";
}

/** Facts derived only from the executable plan and local result, never model prose. */
export function queryProvenance(plan: QueryPlan, matchedRows: number, totalRows: number): QueryProvenanceItem[] {
  const columns = new Set<string>();
  if (plan.filter) filterColumns(plan.filter).forEach((column) => columns.add(column));
  if (plan.aggregate?.column) columns.add(plan.aggregate.column);
  if (plan.aggregate?.groupBy) columns.add(plan.aggregate.groupBy);
  if (plan.topN?.column) columns.add(plan.topN.column);

  const items: QueryProvenanceItem[] = [{ label: "Operation", value: operation(plan) }];
  if (columns.size > 0) items.push({ label: "Columns", value: [...columns].join(", ") });
  if (plan.filter) items.push({ label: "Filter", value: describeFilter(plan.filter) });
  items.push({ label: "Rows", value: `${matchedRows.toLocaleString()} of ${totalRows.toLocaleString()}` });
  if (plan.spatial) {
    const { anchor } = plan.spatial;
    const assumption = anchor.kind === "point" ? "point" : anchor.kind === "zip_approx" ? "ZIP approximation" : "region approximation";
    items.push({
      label: "Geography",
      value: `${anchor.label} · ${anchor.lat.toFixed(4)}, ${anchor.lng.toFixed(4)} · ${Math.round(anchor.confidence * 100)}% confidence · ${assumption}`,
    });
  }
  return items;
}
